// Settings → «الخريطة»: where the company works (whole governorates and/or chosen areas) and the map's defaults.
// The work area decides where the map opens, where place search looks (no «العبدلي» in another country) and where the
// public form's map starts. Nothing chosen = the company's whole country.
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { LocationItem } from '@re/form-ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../api/client';
import { loadLocations } from '../api/lookups';
import type { MapSettings } from '../api/map';
import { getUiLanguage, tr } from '../lib/i18n';
import { Button } from './button';
import { TextField } from './text-field';
import { useToast } from './Toast';

const DEFAULTS: MapSettings = {
  workGovernorateIds: [],
  workAreaIds: [],
  lockToWorkArea: false,
  style: 'satellite',
  colorBy: 'kind',
  showApprox: true,
  rentPeriod: 'year',
  sharePrecision: 'approx',
};
const nameOf = (x: { name_ar: string; name_en?: string | null }) => (getUiLanguage() === 'en' && x.name_en?.trim() ? x.name_en : x.name_ar);
const same = (a: MapSettings, b: MapSettings) => JSON.stringify(a) === JSON.stringify(b);

/** one governorate of the work area: all of it, or some of its areas */
interface GovChoice {
  id: number;
  whole: boolean;
  areaIds: number[];
}

export function MapSettingsTab({ value, countryId, canEdit }: { value: MapSettings | undefined; countryId: number; canEdit: boolean }) {
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const saved = useMemo(() => ({ ...DEFAULTS, ...(value ?? {}) }), [value]);
  const [m, setM] = useState<MapSettings>(saved);
  /** governorates switched to «مناطق محددة» before any area is picked (they have nothing in the settings yet) */
  const [pending, setPending] = useState<number[]>([]);
  useEffect(() => {
    setM(saved);
    setPending([]);
  }, [saved]);

  const govs = useQuery({ queryKey: ['locations', 'governorates', countryId], queryFn: () => loadLocations('governorates', countryId), staleTime: 10 * 60_000 });
  // the areas of the country (to name chosen areas and pick new ones)
  const areas = useQuery({
    queryKey: ['locations', 'areas-of', countryId],
    queryFn: async () => {
      const all = await Promise.all((govs.data ?? []).map((g) => loadLocations('areas', g.id)));
      return all.flat();
    },
    enabled: !!govs.data?.length,
    staleTime: 10 * 60_000,
  });
  const areaById = useMemo(() => new Map((areas.data ?? []).map((a) => [a.id, a])), [areas.data]);

  // the choices, per governorate
  const choices = useMemo<GovChoice[]>(() => {
    const out = new Map<number, GovChoice>();
    for (const id of m.workGovernorateIds) out.set(id, { id, whole: true, areaIds: [] });
    for (const aid of m.workAreaIds) {
      const gid = areaById.get(aid)?.parent_id;
      if (!gid || out.get(gid)?.whole) continue;
      const c = out.get(gid) ?? { id: gid, whole: false, areaIds: [] };
      c.areaIds.push(aid);
      out.set(gid, c);
    }
    for (const gid of pending) if (!out.has(gid)) out.set(gid, { id: gid, whole: false, areaIds: [] });
    return [...out.values()];
  }, [m.workGovernorateIds, m.workAreaIds, areaById, pending]);
  const chosen = (gid: number) => choices.find((c) => c.id === gid);

  const setChoices = (next: GovChoice[]) => {
    setPending(next.filter((c) => !c.whole && !c.areaIds.length).map((c) => c.id));
    setM((cur) => ({
      ...cur,
      workGovernorateIds: next.filter((c) => c.whole).map((c) => c.id),
      workAreaIds: next.filter((c) => !c.whole).flatMap((c) => c.areaIds),
    }));
  };
  const toggleGov = (gid: number) => {
    const c = chosen(gid);
    setChoices(c ? choices.filter((x) => x.id !== gid) : [...choices, { id: gid, whole: true, areaIds: [] }]);
  };
  const setGov = (gid: number, patch: Partial<GovChoice>) => setChoices(choices.map((c) => (c.id === gid ? { ...c, ...patch } : c)));

  const suggest = useMutation({
    mutationFn: () => api<{ governorates: { id: number; n: number }[]; areas: { id: number; governorateId: number; n: number }[] }>('/map-view/suggest'),
    onSuccess: (r) => {
      if (!r.governorates.length) return toast(tr('لا توجد بيانات كافية للاقتراح بعد', 'Not enough data to suggest yet'), 'info');
      // the governorates that hold ~95% of the company's entries, each as a whole
      const total = r.governorates.reduce((s, g) => s + g.n, 0);
      const pick: number[] = [];
      let acc = 0;
      for (const g of r.governorates) {
        pick.push(g.id);
        acc += g.n;
        if (acc / total >= 0.95) break;
      }
      setChoices(pick.map((id) => ({ id, whole: true, areaIds: [] })));
      toast(tr('تم اقتراح المناطق من بياناتك — راجعها ثم احفظ', 'Suggested from your data — review, then save'));
    },
    onError: toastError,
  });

  const save = useMutation({
    mutationFn: () => api('/settings', { method: 'PATCH', body: { map: m } }),
    onSuccess: () => {
      toast(tr('تم حفظ إعدادات الخريطة', 'Map settings saved'));
      void qc.invalidateQueries({ queryKey: ['settings'] });
      void qc.invalidateQueries({ queryKey: ['map-view'] });
    },
    onError: toastError,
  });

  const summary = choices.length
    ? choices.map((c) => {
        const g = govs.data?.find((x) => x.id === c.id);
        const gName = g ? nameOf(g) : '…';
        if (c.whole) return gName;
        if (!c.areaIds.length) return `${gName}: ${tr('اختر المناطق', 'choose the areas')}`;
        return `${gName}: ${c.areaIds.map((id) => (areaById.get(id) ? nameOf(areaById.get(id)!) : '…')).join(tr('، ', ', '))}`;
      }).join(tr(' · ', ' · '))
    : tr('كل الدولة', 'The whole country');

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6">{tr('منطقة العمل', 'Work area')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {tr('المحافظات أو المناطق التي تعمل فيها الشركة. الخريطة تفتح عليها، والبحث عن الأماكن يبقى داخلها، وخريطة النموذج العام تبدأ منها. بدون اختيار = كل الدولة.', 'Where the company works. The map opens there, place search stays inside, and the public form’s map starts there. Nothing chosen = the whole country.')}
        </Typography>
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {(govs.data ?? []).map((g) => {
          const on = !!chosen(g.id);
          return <Chip key={g.id} label={nameOf(g)} color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'} onClick={canEdit ? () => toggleGov(g.id) : undefined} />;
        })}
      </Stack>
      {canEdit && (
        <Box>
          <Button size="small" startIcon={<AutoAwesomeOutlinedIcon />} loading={suggest.isPending} onClick={() => suggest.mutate()}>
            {tr('اقترح من بياناتي', 'Suggest from my data')}
          </Button>
        </Box>
      )}

      {choices.map((c) => {
        const g = govs.data?.find((x) => x.id === c.id);
        if (!g) return null;
        const options = (areas.data ?? []).filter((a) => a.parent_id === c.id);
        return (
          <Paper key={c.id} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Typography fontWeight={700} sx={{ minWidth: 120 }}>{nameOf(g)}</Typography>
              <RadioGroup row value={c.whole ? 'whole' : 'some'} onChange={(e) => setGov(c.id, { whole: e.target.value === 'whole' })}>
                <FormControlLabel value="whole" control={<Radio size="small" />} label={tr('كل المحافظة', 'The whole governorate')} disabled={!canEdit} />
                <FormControlLabel value="some" control={<Radio size="small" />} label={tr('مناطق محددة', 'Chosen areas')} disabled={!canEdit} />
              </RadioGroup>
            </Stack>
            {!c.whole && (
              <Autocomplete
                multiple
                size="small"
                sx={{ mt: 1 }}
                disabled={!canEdit}
                loading={areas.isLoading}
                options={options}
                getOptionLabel={(o: LocationItem) => nameOf(o)}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={options.filter((o) => c.areaIds.includes(o.id))}
                onChange={(_, v) => setGov(c.id, { areaIds: v.map((x) => x.id) })}
                renderInput={(p) => <TextField {...p} placeholder={c.areaIds.length ? '' : tr('ابحث واختر المناطق…', 'Search and choose areas…')} />}
                noOptionsText={tr('لا نتائج', 'No results')}
              />
            )}
          </Paper>
        );
      })}
      <Alert severity="info" variant="outlined">{tr('ستعمل الخريطة ضمن', 'The map will work within')}: <b>{summary}</b></Alert>
      <FormControlLabel
        control={<Switch checked={m.lockToWorkArea} disabled={!canEdit} onChange={(e) => setM({ ...m, lockToWorkArea: e.target.checked })} />}
        label={
          <Box>
            <Typography variant="body2">{tr('ثبّت الخريطة داخل منطقة العمل', 'Keep the map inside the work area')}</Typography>
            <Typography variant="caption" color="text.secondary">{tr('بدونها تبدأ الخريطة من منطقة العمل ويمكن التحرك خارجها (لعقار بعيد مثلاً).', 'Without it the map starts there but can move outside (e.g. a property far away).')}</Typography>
          </Box>
        }
      />

      <Divider />
      <Box>
        <Typography variant="h6">{tr('الإعدادات الافتراضية', 'Defaults')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{tr('كيف تفتح الخريطة لكل الموظفين (يستطيع كل موظف تغييرها أثناء العمل).', 'How the map opens for everyone (each employee can still change it while working).')}</Typography>
      </Box>
      <Stack spacing={2}>
        <Row label={tr('شكل الخريطة', 'Map style')}>
          <ToggleButtonGroup size="small" exclusive value={m.style} disabled={!canEdit} onChange={(_, v) => v && setM({ ...m, style: v })}>
            <ToggleButton value="satellite">{tr('قمر صناعي', 'Satellite')}</ToggleButton>
            <ToggleButton value="streets">{tr('شوارع', 'Streets')}</ToggleButton>
          </ToggleButtonGroup>
        </Row>
        <Row label={tr('لون الدبابيس', 'Pin colours')}>
          <ToggleButtonGroup size="small" exclusive value={m.colorBy} disabled={!canEdit} onChange={(_, v) => v && setM({ ...m, colorBy: v })}>
            <ToggleButton value="kind">{tr('بيع / إيجار', 'Sale / rent')}</ToggleButton>
            <ToggleButton value="ppm">{tr('سعر المتر', 'Price per m²')}</ToggleButton>
          </ToggleButtonGroup>
        </Row>
        <Row label={tr('سعر الإيجار يعني', 'A rent price is')}>
          <ToggleButtonGroup size="small" exclusive value={m.rentPeriod} disabled={!canEdit} onChange={(_, v) => v && setM({ ...m, rentPeriod: v })}>
            <ToggleButton value="year">{tr('سنوي', 'Yearly')}</ToggleButton>
            <ToggleButton value="month">{tr('شهري', 'Monthly')}</ToggleButton>
          </ToggleButtonGroup>
        </Row>
        <FormControlLabel
          control={<Switch checked={m.showApprox} disabled={!canEdit} onChange={(e) => setM({ ...m, showApprox: e.target.checked })} />}
          label={tr('أظهر العقارات بدون موقع دقيق في منتصف منطقتها (تقريبي)', 'Show properties without an exact pin in the middle of their area (approximate)')}
        />
      </Stack>

      <Divider />
      <Box>
        <Typography variant="h6">{tr('الموقع في صفحة مشاركة العقار', 'Location on a shared property page')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{tr('ما يراه الزبون عندما ترسل له رابط عقار.', 'What a customer sees when you send them a property link.')}</Typography>
      </Box>
      <RadioGroup value={m.sharePrecision} onChange={(e) => setM({ ...m, sharePrecision: e.target.value as MapSettings['sharePrecision'] })}>
        <FormControlLabel disabled={!canEdit} value="approx" control={<Radio />} label={<Opt t={tr('تقريبي (دائرة)', 'Approximate (a circle)')} d={tr('دائرة حول المكان بدون العمارة — يتواصل الزبون معكم للعنوان. (مُوصى به)', 'A circle around the place without the building — the customer contacts you for the address. (Recommended)')} />} />
        <FormControlLabel disabled={!canEdit} value="exact" control={<Radio />} label={<Opt t={tr('دقيق', 'Exact')} d={tr('الدبوس على العقار مع فتحه في خرائط Google.', 'The pin on the property, opens in Google Maps.')} />} />
        <FormControlLabel disabled={!canEdit} value="hidden" control={<Radio />} label={<Opt t={tr('مخفي', 'Hidden')} d={tr('بدون خريطة، المنطقة بالاسم فقط.', 'No map, the area by name only.')} />} />
      </RadioGroup>

      {canEdit && (
        <Box>
          <Button variant="contained" disabled={same(m, saved)} loading={save.isPending} onClick={() => save.mutate()}>{tr('حفظ إعدادات الخريطة', 'Save map settings')}</Button>
        </Box>
      )}
    </Stack>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
      <Typography variant="body2" sx={{ minWidth: 150 }}>{label}</Typography>
      {children}
    </Stack>
  );
}
function Opt({ t, d }: { t: string; d: string }) {
  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="body2" fontWeight={600}>{t}</Typography>
      <Typography variant="caption" color="text.secondary">{d}</Typography>
    </Box>
  );
}
